/**
 * CSV Data Utilities
 * Pure functions for sorting, filtering, paginating, and exporting CSV data
 */

/**
 * Sort data by a key
 * @param {Array} data - Array of row objects
 * @param {string} sortKey - Key to sort by
 * @param {string} sortDirection - 'asc' or 'desc'
 * @returns {Array} Sorted copy of data
 */
export const sortData = (data, sortKey, sortDirection) => {
  if (!sortKey || !data) return data;

  return [...data].sort((a, b) => {
    const aVal = a[sortKey] ?? '';
    const bVal = b[sortKey] ?? '';

    // Try numeric comparison first
    const aNum = parseFloat(aVal);
    const bNum = parseFloat(bVal);
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
    }

    // String comparison
    const comparison = String(aVal).localeCompare(String(bVal));
    return sortDirection === 'asc' ? comparison : -comparison;
  });
};

/**
 * Filter data by search term across specified headers
 * @param {Array} data - Array of row objects
 * @param {string} searchTerm - Term to search for
 * @param {Array} headers - Column headers to search
 * @returns {Array} Filtered data
 */
export const filterData = (data, searchTerm, headers) => {
  if (!searchTerm || !data) return data;

  const term = searchTerm.toLowerCase();
  return data.filter((row) =>
    headers.some((header) =>
      String(row[header] ?? '').toLowerCase().includes(term)
    )
  );
};

/**
 * Paginate data
 * @param {Array} data - Array of row objects
 * @param {number} page - Current page (1-indexed)
 * @param {number} pageSize - Items per page
 * @returns {Array} Paginated slice of data
 */
export const paginateData = (data, page, pageSize) => {
  if (!data) return [];
  const start = (page - 1) * pageSize;
  return data.slice(start, start + pageSize);
};

/**
 * Escape a cell value for CSV format
 * @param {*} cell - Cell value
 * @returns {string} Escaped string safe for CSV
 */
export const escapeCSVCell = (cell) => {
  const str = String(cell ?? '');
  // Card 53649: a bare CR is a record separator in its own right, so leaving it un-quoted split
  // one logical row into two on export. CRLF was only ever safe incidentally, via the \n test.
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

/**
 * Convert headers and data to CSV string
 * @param {Array} headers - Column headers
 * @param {Array} data - Array of row objects
 * @returns {string} CSV formatted string
 */
export const convertToCSV = (headers, data) => {
  const headerRow = headers.map(escapeCSVCell).join(',');
  const dataRows = data.map((row) =>
    headers.map((h) => escapeCSVCell(row[h])).join(',')
  );

  return [headerRow, ...dataRows].join('\n');
};

/**
 * Trigger a CSV file download in the browser
 * @param {string} content - CSV content string
 * @param {string} filename - Name for the downloaded file
 */
export const downloadCSV = (content, filename) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

/**
 * Calculate total pages for pagination
 * @param {number} totalItems - Total number of items
 * @param {number} pageSize - Items per page
 * @returns {number} Total pages
 */
export const calculateTotalPages = (totalItems, pageSize) => {
  return Math.ceil(totalItems / pageSize);
};
